<template>
  <div class="col-sm-12">

    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 class="mb-0">Aplicación de pagos individuales</h4>
          <small class="text-muted" v-if="rfc">RFC: {{ rfc }}</small>
        </div>
        <div class="indicador indicador-verde">
          <div class="indicador-label">Primas en depósito</div>
          <div class="indicador-valor">{{ formatCurrency(primasDeposito) }}</div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
      <span class="ms-2 text-muted small">Cargando pagos pendientes...</span>
    </div>

    <div v-else-if="!polizas.length" class="alert alert-warning">
      No se recibió ninguna póliza seleccionada. Regresa a Pagos Individuales y selecciona al menos una.
    </div>

    <template v-else>
      <div v-for="grupo in gruposParcialidades" :key="grupo.polizaVersionId" class="card mb-3">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h6 class="mb-0 fw-bold">
              Póliza {{ grupo.numeroPoliza || grupo.polizaVersionId }}
              <span v-if="grupo.polizaVersion != null" class="text-muted fw-normal">· Versión {{ grupo.polizaVersion }}</span>
            </h6>
            <div class="text-end">
              <small class="text-muted d-block">{{ grupo.parcialidades.length }} pago(s) pendiente(s)</small>
              <button
                class="btn btn-sm btn-primary mt-1"
                :disabled="primasDeposito <= 0"
                @click="aplicarPago(grupo)"
              >
                <vue-feather type="zap" size="14" class="me-1" /> Aplicar
              </button>
            </div>
          </div>
          <div class="row g-3 mt-1">
            <div class="col-auto">
              <small class="text-muted d-block">Prima total</small>
              <span class="fw-bold">{{ formatCurrency(grupo.primaTotal) }}</span>
            </div>
            <div class="col-auto">
              <small class="text-muted d-block">Pago pendiente</small>
              <span class="fw-bold text-danger">{{ formatCurrency(pagoPendienteGrupo(grupo)) }}</span>
            </div>
            <div class="col-auto">
              <small class="text-muted d-block">Pago realizado</small>
              <span class="fw-bold text-success">{{ formatCurrency(pagoRealizadoGrupo(grupo)) }}</span>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <div v-if="grupo.error" class="alert alert-danger m-3 mb-0 py-2">{{ grupo.error }}</div>
          <div v-else-if="!grupo.parcialidades.length" class="text-center py-4 text-muted small">
            Esta póliza no tiene pagos pendientes.
          </div>
          <DataTable v-else :rows="grupo.parcialidades" :columns="grupo.columns" :show-actions="false">
            <template v-if="grupo.montoKey" #[`cell-${grupo.montoKey}`]="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template v-if="grupo.montoPendienteKey" #[`cell-${grupo.montoPendienteKey}`]="{ value, item }">
              {{ formatCurrency(value - (item._montoAplicado || 0)) }}
            </template>
            <template v-if="grupo.estadoKey" #[`cell-${grupo.estadoKey}`]="{ value }">
              {{ estadoLabel(value) }}
            </template>
            <template #cell-_pagar="{ item }">
              <input type="checkbox" v-model="item._pagar" @change="onTogglePagar(grupo, item)" />
            </template>
          </DataTable>
        </div>
      </div>
    </template>

    <div class="mt-3">
      <button class="btn btn-outline-secondary" @click="volver">
        <vue-feather type="arrow-left" size="14" class="me-1" /> Volver
      </button>
    </div>

  </div>
</template>

<script>
import { fetchAllItems } from '@/utils/listResponse';
import DataTable from '@/components/DataTable.vue';

const HIDDEN_COLUMNS = ['polizaversionid', 'polizaversion', 'isactive', 'created', 'updated'];

// TODO: confirmar con backend el catálogo real de estados de parcialidad.
const ESTADOS_PARCIALIDAD = {
  0: 'Pendiente',
  1: 'Pagada',
  2: 'Vencida',
  3: 'Cancelada',
};

function getField(row, key) {
  const found = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
  return found ? row[found] : undefined;
}

export default {
  name: 'AplicacionPagosIndividuales',
  components: { DataTable },

  data() {
    return {
      loading: false,
      polizas: [],
      gruposParcialidades: [],
      rfc: '',
      primasDepositoOriginal: 0,
    };
  },

  computed: {
    totalAplicado() {
      return this.gruposParcialidades.reduce((total, grupo) => total + this.montoAplicadoGrupo(grupo), 0);
    },

    primasDeposito() {
      return this.primasDepositoOriginal - this.totalAplicado;
    },
  },

  created() {
    const { polizas, rfc, primasDeposito } = this.$route.query;
    this.rfc = rfc || '';
    this.primasDepositoOriginal = Number(primasDeposito) || 0;
    try {
      this.polizas = polizas ? JSON.parse(polizas) : [];
    } catch {
      this.polizas = [];
    }
    this.cargarParcialidades();
  },

  methods: {
    async cargarParcialidades() {
      if (!this.polizas.length) return;
      this.loading = true;
      this.gruposParcialidades = await Promise.all(this.polizas.map(async (pv) => {
        try {
          const { items: rawData } = await fetchAllItems(`parcialidades/poliza-version/${pv.id}`);
          const data = rawData.map(row => ({ ...row, _pagar: false, _montoAplicado: 0 }));
          const columns = rawData.length
            ? Object.keys(rawData[0])
                .filter(k => !HIDDEN_COLUMNS.includes(k.toLowerCase()))
                .map(k => ({ text: this.friendlyLabel(k), value: k }))
            : [];
          columns.push({ text: 'Pagar', value: '_pagar' });

          const montoKey = this.findColumnKey(columns, 'monto');
          const montoPendienteKey = this.findColumnKey(columns, 'montopendiente');
          const primaTotal = montoKey ? rawData.reduce((sum, r) => sum + (Number(getField(r, montoKey)) || 0), 0) : 0;

          return {
            polizaVersionId: pv.id,
            numeroPoliza: pv.numeroPoliza,
            polizaVersion: rawData.length ? getField(rawData[0], 'polizaVersion') : null,
            parcialidades: data,
            columns,
            montoKey,
            montoPendienteKey,
            estadoKey: this.findColumnKey(columns, 'estado'),
            primaTotal,
            error: null,
          };
        } catch (err) {
          console.error('Error al cargar parcialidades pendientes:', err);
          return {
            polizaVersionId: pv.id,
            numeroPoliza: pv.numeroPoliza,
            parcialidades: [],
            columns: [],
            error: 'No se pudieron cargar los pagos pendientes de esta póliza.',
          };
        }
      }));
      this.gruposParcialidades.forEach(grupo => this.aplicarPago(grupo));
      this.loading = false;
    },

    montoAplicadoGrupo(grupo) {
      return grupo.parcialidades.reduce((sum, row) => sum + (row._montoAplicado || 0), 0);
    },

    onTogglePagar(grupo, row) {
      if (!grupo.montoPendienteKey) return;
      const pendienteOriginal = Number(getField(row, grupo.montoPendienteKey)) || 0;
      if (row._pagar) {
        const disponible = Math.max(this.primasDeposito, 0);
        row._montoAplicado = Math.min(pendienteOriginal, disponible);
      } else {
        row._montoAplicado = 0;
      }
    },

    pagoPendienteGrupo(grupo) {
      if (!grupo.montoPendienteKey) return 0;
      const totalPendienteOriginal = grupo.parcialidades.reduce(
        (sum, row) => sum + (Number(getField(row, grupo.montoPendienteKey)) || 0),
        0
      );
      return totalPendienteOriginal - this.montoAplicadoGrupo(grupo);
    },

    pagoRealizadoGrupo(grupo) {
      return grupo.primaTotal - this.pagoPendienteGrupo(grupo);
    },

    // TODO: sustituir por la llamada al endpoint real cuando esté disponible.
    aplicarPago(grupo) {
      if (!grupo.montoPendienteKey) return;
      let disponible = this.primasDeposito;
      for (const row of grupo.parcialidades) {
        if (row._pagar) continue;
        const pendiente = Number(getField(row, grupo.montoPendienteKey)) || 0;
        if (pendiente <= 0) continue;
        if (pendiente <= disponible) {
          row._pagar = true;
          row._montoAplicado = pendiente;
          disponible -= pendiente;
        } else {
          break;
        }
      }
    },

    friendlyLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    findColumnKey(columns, name) {
      const col = columns.find(c => c.value.toLowerCase() === name);
      return col ? col.value : null;
    },

    estadoLabel(value) {
      const key = Number(value);
      return Object.prototype.hasOwnProperty.call(ESTADOS_PARCIALIDAD, key)
        ? ESTADOS_PARCIALIDAD[key]
        : String(value ?? '—');
    },

    formatCurrency(value) {
      if (value == null || value === '') return '—';
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    },

    volver() {
      this.$router.push({ name: 'individual' });
    },
  },
};
</script>

<style scoped>
.indicador {
  border-radius: 8px;
  padding: 8px 16px;
  min-width: 180px;
  text-align: center;
}
.indicador-verde {
  background-color: #f0fff4;
  border: 1px solid #c3e6cb;
}
.indicador-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6c757d;
}
.indicador-verde .indicador-valor {
  font-size: 15px;
  font-weight: 700;
  color: #28a745;
}
</style>
